'use client'

import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react-dom-interactions'
import React from 'react'

import { useEnhancedEffect, useLockBodyScroll } from '../../../hooks'
import { Arrow } from '../../../icons'
import { Input } from '../../Input'
import { Option } from '../../SelectField/SelectInput/Option'
import { Container, Popup } from '../../SelectField/SelectInput/styles'
import { normalizeForSearch } from '../normalize'

export interface SearchableOption {
  label: string
  value: string
  /**
   * label / value 以外で検索ヒットさせたい追加キーワード。
   * 例: 漢字ラベル ("日本") に対する読み ("にほん", "にっぽん") を持たせる用途。
   */
  keywords?: string[]
}

export interface SearchableSelectInputProps {
  id?: string
  name?: string
  className?: string
  options: SearchableOption[] | Readonly<SearchableOption[]>
  value?: string
  placeholder?: string
  noMatchMessage?: string
  hasError?: boolean
  disabled?: boolean
  autoWidth?: boolean
  placement?: Placement
  disableBodyScrollLock?: boolean
  onBlur?: (e: React.FocusEvent) => void
  onChange?: (value: string) => void
}

const SearchableSelectInputInner = (
  {
    id,
    className,
    name,
    value,
    options,
    placeholder,
    noMatchMessage = '一致する候補がありません',
    hasError = false,
    disabled = false,
    onBlur,
    onChange,
    autoWidth,
    placement = 'bottom',
    disableBodyScrollLock = false,
  }: SearchableSelectInputProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const listItemsRef = React.useRef<(HTMLLIElement | null)[]>([])

  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  // -1 = まだナビゲートしていない。Enter を押しても何も commit しない。
  const [activeIndex, setActiveIndex] = React.useState<number>(-1)
  const [pointer, setPointer] = React.useState(false)

  const selectedLabel = React.useMemo(
    () => options.find((o) => o.value === value)?.label ?? '',
    [options, value],
  )

  const displayValue = open ? query : selectedLabel

  const filtered = React.useMemo(() => {
    if (!open || query === '' || query === selectedLabel) return [...options]
    const q = normalizeForSearch(query)
    return options.filter((o) => {
      if (normalizeForSearch(o.label).includes(q)) return true
      if (normalizeForSearch(o.value).includes(q)) return true
      if (o.keywords) {
        for (const kw of o.keywords) {
          if (normalizeForSearch(kw).includes(q)) return true
        }
      }
      return false
    })
  }, [open, query, options, selectedLabel])

  // 候補数が変わって activeIndex がはみ出した場合のリセット
  React.useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(-1)
  }, [filtered, activeIndex])

  if (!open && pointer) setPointer(false)

  const { x, y, reference, floating, strategy, context } = useFloating({
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
    placement,
  })

  // virtual list navigation は手動実装するため useListNavigation は使わない
  // (0.13.0 だと composed onKeyDown 経路で arrow が拾えなかったため)
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [useRole(context, { role: 'listbox' }), useDismiss(context)],
  )

  useLockBodyScroll(disableBodyScrollLock ? false : open)

  // アクティブな項目を見える範囲にスクロール
  useEnhancedEffect(() => {
    if (!open || activeIndex < 0 || pointer) return
    requestAnimationFrame(() => {
      listItemsRef.current[activeIndex]?.scrollIntoView({ block: 'nearest' })
    })
  }, [open, activeIndex, pointer])

  const setInputRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node
      reference(node)
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [reference, ref],
  )

  const commitSelection = (next: string) => {
    onChange?.(next)
    setQuery('')
    setActiveIndex(-1)
    setOpen(false)
    inputRef.current?.blur()
  }

  const optionDomId = (optValue: string) =>
    id ? `${id}_option_${optValue}` : undefined

  const activeId =
    activeIndex >= 0 && filtered[activeIndex]
      ? optionDomId(filtered[activeIndex].value)
      : undefined

  return (
    <Container ref={containerRef} className={className} autoWidth={autoWidth}>
      <Input
        variant="input"
        id={id}
        type="text"
        autoComplete="off"
        spellCheck={false}
        value={displayValue}
        placeholder={placeholder}
        open={open}
        disabled={disabled}
        autoWidth={autoWidth}
        error={hasError}
        iconAfter={<Arrow direction="down" />}
        {...getReferenceProps({
          ref: setInputRef,
          role: 'combobox',
          'aria-controls': id ? `${id}_list` : undefined,
          'aria-expanded': open,
          'aria-autocomplete': 'list',
          'aria-activedescendant': activeId,
          onFocus: () => {
            setQuery('')
            setOpen(true)
          },
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            const next = event.target.value
            setQuery(next)
            if (!open) setOpen(true)
            // 自動ハイライトしない (Enter で先頭候補が誤確定されるのを防ぐ)
            setActiveIndex(-1)
          },
          onBlur: (event: React.FocusEvent) => {
            onBlur?.(event)
          },
          onKeyDown: (event: React.KeyboardEvent) => {
            // IME 変換中は無視 (Enter で確定する間)
            if (event.nativeEvent.isComposing) return

            setPointer(false)

            if (event.key === 'ArrowDown') {
              event.preventDefault()
              if (filtered.length === 0) return
              setActiveIndex((curr) => (curr + 1) % filtered.length)
              return
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault()
              if (filtered.length === 0) return
              setActiveIndex((curr) =>
                curr <= 0 ? filtered.length - 1 : curr - 1,
              )
              return
            }
            if (event.key === 'Home') {
              if (filtered.length === 0) return
              event.preventDefault()
              setActiveIndex(0)
              return
            }
            if (event.key === 'End') {
              if (filtered.length === 0) return
              event.preventDefault()
              setActiveIndex(filtered.length - 1)
              return
            }
            if (
              event.key === 'Enter' &&
              activeIndex >= 0 &&
              filtered[activeIndex]
            ) {
              event.preventDefault()
              commitSelection(filtered[activeIndex].value)
            }
            if (event.key === 'Escape') {
              setOpen(false)
              inputRef.current?.blur()
            }
            if (event.key === 'Tab') {
              setOpen(false)
            }
          },
        })}
      />

      {open ? (
        <Popup
          role="listbox"
          id={id ? `${id}_list` : undefined}
          tabIndex={-1}
          open={open}
          {...getFloatingProps({
            ref: floating,
            style: {
              zIndex: 1,
              position: strategy,
              top: 0,
              left: 0,
              width: autoWidth ? 'auto' : '100%',
              transform: `translate(${Math.round(x ?? 0)}px,${Math.round(
                y ?? 0,
              )}px)`,
            },
            onPointerMove() {
              setPointer(true)
            },
          })}
        >
          {filtered.length === 0 ? (
            <Option
              key="__no_match__"
              tabIndex={-1}
              active={false}
              selected={false}
              value=""
            >
              {noMatchMessage}
            </Option>
          ) : (
            filtered.map((option, index) => (
              <Option
                key={option.value}
                id={optionDomId(option.value)}
                ref={(node) => {
                  listItemsRef.current[index] = node
                }}
                tabIndex={-1}
                active={activeIndex === index}
                selected={option.value === value}
                value={option.value}
                {...getItemProps({
                  // blur が click より先に発火するのを防ぐ
                  onMouseDown: (e: React.MouseEvent) => {
                    e.preventDefault()
                  },
                  onMouseEnter: () => setActiveIndex(index),
                  onClick: () => commitSelection(option.value),
                })}
              >
                {option.label}
              </Option>
            ))
          )}
        </Popup>
      ) : null}

      {name && value !== undefined && (
        <input type="hidden" name={name} value={value} />
      )}
    </Container>
  )
}

export const SearchableSelectInput = React.forwardRef(
  SearchableSelectInputInner,
)
